import { SIMAElementClassifier, } from "./types";
/**
 * Splits a CSV line while handling quotes properly.
 */
function parseCSVLine(line) {
    const tokens = [];
    let currentToken = "";
    let insideQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
            insideQuotes = !insideQuotes;
        }
        else if (char === "," && !insideQuotes) {
            tokens.push(currentToken.trim());
            currentToken = "";
        }
        else {
            currentToken += char;
        }
    }
    tokens.push(currentToken.trim());
    return tokens;
}
function parseFloatSafe(val, defaultVal = 0) {
    if (!val || val.trim() === "")
        return defaultVal;
    const num = Number.parseFloat(val.trim());
    return Number.isNaN(num) ? defaultVal : num;
}
function parseIntSafe(val, defaultVal = 0) {
    if (!val || val.trim() === "")
        return defaultVal;
    const num = Number.parseInt(val.trim(), 10);
    return Number.isNaN(num) ? defaultVal : num;
}
/**
 * Parses SIMA formatted text into an ISIMAFormat object.
 *
 * Supports:
 * - File metadata & comments (Z00, Z01, G00)
 * - Survey point coordinates (A00..A99)
 * - Horizontal alignments/routes (F00..F99, B01, B03, C00..C99)
 * - Longitudinal profiles (H00..H99, H01)
 * - Cross-section groups & transverse points (I00..I99, I01, I02)
 * - Cadastral parcels / lots (D00..D99, D01)
 * - Triangulation / Sanshahō area calculation (E00..E99, E01, E03)
 *
 * NOTE ON ENCODING: Standard Japanese SIMA files exported by CAD software (e.g. EX-TREND)
 * are often encoded in Shift-JIS (CP932). This function expects a pre-decoded UTF-8 string.
 * Callers reading raw binary/Buffer content should decode using `new TextDecoder("shift-jis").decode(buffer)`
 * before passing `content` to `parseSIMA`.
 *
 * @param content SIMA file string content (UTF-8 decoded string)
 * @returns Parsed ISIMAFormat structure
 */
export function parseSIMA(content) {
    let comment;
    let fileAttributeData = {
        classifier: SIMAElementClassifier.FILE_ATTRIBUTE,
        version: 2,
        site_name: "",
    };
    const surveyPoints = [];
    const simaRoutes = [];
    const profiles = [];
    const crossSections = [];
    const parcels = [];
    const triangulations = [];
    const warnings = [];
    // State machine state
    let currentRoute = null;
    let currentProfile = null;
    let currentCrossSectionGroup = null;
    let currentCrossSectionStation = null;
    let currentParcel = null;
    let currentTriangulation = null;
    let currentTriangle = null;
    function closeActiveRoute(reason, line) {
        if (currentRoute) {
            simaRoutes.push(currentRoute);
            currentRoute = null;
            if (reason !== "normal") {
                warnings.push({
                    type: "MISSING_DELIMITER",
                    message: `Route block was not closed with F99 before ${reason}.`,
                    line,
                });
            }
        }
    }
    function closeActiveProfile(reason, line) {
        if (currentProfile) {
            profiles.push(currentProfile);
            currentProfile = null;
            if (reason !== "normal") {
                warnings.push({
                    type: "MISSING_DELIMITER",
                    message: `Profile block was not closed with H99 before ${reason}.`,
                    line,
                });
            }
        }
    }
    function closeActiveCrossSection(reason, line) {
        if (currentCrossSectionGroup) {
            if (currentCrossSectionStation) {
                currentCrossSectionGroup.stations.push(currentCrossSectionStation);
                currentCrossSectionStation = null;
            }
            crossSections.push(currentCrossSectionGroup);
            currentCrossSectionGroup = null;
            if (reason !== "normal") {
                warnings.push({
                    type: "MISSING_DELIMITER",
                    message: `Cross section group was not closed with I99 before ${reason}.`,
                    line,
                });
            }
        }
    }
    function closeActiveParcel(reason, line) {
        if (currentParcel) {
            parcels.push(currentParcel);
            currentParcel = null;
            if (reason !== "normal") {
                warnings.push({
                    type: "MISSING_DELIMITER",
                    message: `Parcel block was not closed with D99 before ${reason}.`,
                    line,
                });
            }
        }
    }
    function closeActiveTriangulation(reason, line) {
        if (currentTriangulation) {
            if (currentTriangle?.vertex) {
                currentTriangulation.triangles.push(currentTriangle);
                currentTriangle = null;
            }
            triangulations.push(currentTriangulation);
            currentTriangulation = null;
            if (reason !== "normal") {
                warnings.push({
                    type: "MISSING_DELIMITER",
                    message: `Triangulation block was not closed with E99 before ${reason}.`,
                    line,
                });
            }
        }
    }
    function closeAllOpenBlocks(except, line) {
        if (except !== "route")
            closeActiveRoute(except, line);
        if (except !== "profile")
            closeActiveProfile(except, line);
        if (except !== "crossSection")
            closeActiveCrossSection(except, line);
        if (except !== "parcel")
            closeActiveParcel(except, line);
        if (except !== "triangulation")
            closeActiveTriangulation(except, line);
    }
    const lines = content.split(/\r?\n/);
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const rawLine = lines[lineIndex];
        const line = rawLine.trim();
        if (!line) {
            continue;
        }
        const tokens = parseCSVLine(line);
        const classifier = tokens[0];
        switch (classifier) {
            // ==========================================
            // Comments & Header (Z00, Z01, G00)
            // ==========================================
            case SIMAElementClassifier.COMMENT:
                {
                    const text = tokens.slice(1).filter(Boolean).join(",").trim();
                    comment = {
                        classifier: SIMAElementClassifier.COMMENT,
                        text,
                    };
                }
                break;
            case SIMAElementClassifier.COMMENT_HEADER:
                break;
            case SIMAElementClassifier.FILE_ATTRIBUTE:
                {
                    const version = parseIntSafe(tokens[1], 2);
                    const site_name = tokens[2] || "";
                    fileAttributeData = {
                        classifier: SIMAElementClassifier.FILE_ATTRIBUTE,
                        version,
                        site_name,
                    };
                }
                break;
            // ==========================================
            // Survey Points (A00..A99)
            // ==========================================
            case SIMAElementClassifier.SURVEY_POINT_BEGIN:
            case SIMAElementClassifier.SURVEY_POINT_END:
                break;
            case SIMAElementClassifier.SURVEY_POINT_COORD:
                {
                    const point_number = parseIntSafe(tokens[1], 0);
                    const point_name = tokens[2] || "";
                    const x = parseFloatSafe(tokens[3], 0);
                    const y = parseFloatSafe(tokens[4], 0);
                    const z = parseFloatSafe(tokens[5], 0);
                    surveyPoints.push({
                        classifier: SIMAElementClassifier.SURVEY_POINT_COORD,
                        point_number,
                        point_name,
                        x,
                        y,
                        z,
                    });
                }
                break;
            // ==========================================
            // Routes / Alignments (F00..F99)
            // ==========================================
            case SIMAElementClassifier.ROUTE_BEGIN:
                {
                    closeAllOpenBlocks("route", lineIndex + 1);
                    closeActiveRoute("new route (F00)", lineIndex + 1);
                    const route_number = parseIntSafe(tokens[1], 1);
                    const group_number = parseIntSafe(tokens[2], 1);
                    const route_name = tokens[3] || "";
                    const route_type = parseIntSafe(tokens[4], 1);
                    currentRoute = {
                        routeAttributes: [
                            {
                                classifier: SIMAElementClassifier.ROUTE_BEGIN,
                                route_number,
                                group_number,
                                route_name,
                                route_type,
                            },
                        ],
                        routeElements: [],
                    };
                }
                break;
            case SIMAElementClassifier.ROUTE_ATTRIBUTES:
                {
                    if (currentRoute) {
                        const additional_distance = parseFloatSafe(tokens[1], 0);
                        const station_interval = tokens[2] && tokens[2].trim() !== ""
                            ? parseFloatSafe(tokens[2])
                            : undefined;
                        const station_intermediate_interval = tokens[3] && tokens[3].trim() !== ""
                            ? parseFloatSafe(tokens[3])
                            : undefined;
                        currentRoute.routeAttributes.push({
                            classifier: SIMAElementClassifier.ROUTE_ATTRIBUTES,
                            additional_distance,
                            station_interval,
                            station_intermediate_interval,
                        });
                    }
                }
                break;
            case SIMAElementClassifier.ROUTE_COORD:
            case SIMAElementClassifier.PARCEL_POINT:
                {
                    const point_number = parseIntSafe(tokens[1], 0);
                    const point_name = tokens[2] || "";
                    if (currentParcel) {
                        currentParcel.points.push({
                            classifier: SIMAElementClassifier.PARCEL_POINT,
                            point_number,
                            point_name,
                        });
                    }
                    else if (currentRoute) {
                        currentRoute.routeElements.push({
                            classifier: SIMAElementClassifier.ROUTE_COORD,
                            point_number,
                            point_name,
                        });
                    }
                }
                break;
            case SIMAElementClassifier.ROUTE_COORD_ADDITIONAL_DISTANCE:
                {
                    if (currentRoute) {
                        const additional_distance = parseFloatSafe(tokens[1], 0);
                        currentRoute.routeElements.push({
                            classifier: SIMAElementClassifier.ROUTE_COORD_ADDITIONAL_DISTANCE,
                            additional_distance,
                        });
                    }
                }
                break;
            case SIMAElementClassifier.CURVE_BEGIN:
                {
                    if (currentRoute) {
                        currentRoute.routeElements.push({
                            classifier: SIMAElementClassifier.CURVE_BEGIN,
                        });
                    }
                }
                break;
            case SIMAElementClassifier.SIMPLE_CURVE:
                {
                    if (currentRoute) {
                        const direction = parseIntSafe(tokens[1], 0);
                        const radius = parseFloatSafe(tokens[2], 0);
                        currentRoute.routeElements.push({
                            classifier: SIMAElementClassifier.SIMPLE_CURVE,
                            direction,
                            radius,
                        });
                    }
                }
                break;
            case SIMAElementClassifier.CLOTHOID_CURVE:
                {
                    if (currentRoute) {
                        const parameterA = parseFloatSafe(tokens[1], 0);
                        const start_radius = parseFloatSafe(tokens[2], 0);
                        const end_radius = parseFloatSafe(tokens[3], 0);
                        currentRoute.routeElements.push({
                            classifier: SIMAElementClassifier.CLOTHOID_CURVE,
                            parameterA,
                            start_radius,
                            end_radius,
                        });
                    }
                }
                break;
            case SIMAElementClassifier.CURVE_END:
                {
                    if (currentRoute) {
                        currentRoute.routeElements.push({
                            classifier: SIMAElementClassifier.CURVE_END,
                        });
                    }
                }
                break;
            case SIMAElementClassifier.ROUTE_END:
                {
                    closeActiveRoute("normal");
                }
                break;
            // ==========================================
            // Longitudinal Profile (H00..H99)
            // ==========================================
            case SIMAElementClassifier.PROFILE_BEGIN:
                {
                    closeAllOpenBlocks("profile", lineIndex + 1);
                    closeActiveProfile("new profile (H00)", lineIndex + 1);
                    const alignment_number = parseIntSafe(tokens[1], 1);
                    const alignment_name = tokens[2] || "";
                    currentProfile = {
                        alignment_number,
                        alignment_name,
                        stations: [],
                    };
                }
                break;
            case SIMAElementClassifier.PROFILE_POINT:
                {
                    if (currentProfile) {
                        const point_name = tokens[1] || "";
                        const chainage = parseFloatSafe(tokens[2], 0);
                        const ground_elevation = parseFloatSafe(tokens[3], 0);
                        const stake_elevation = parseFloatSafe(tokens[4], 0);
                        const point_type = parseIntSafe(tokens[5], 1);
                        currentProfile.stations.push({
                            classifier: SIMAElementClassifier.PROFILE_POINT,
                            point_name,
                            chainage,
                            ground_elevation,
                            stake_elevation,
                            point_type,
                        });
                    }
                }
                break;
            case SIMAElementClassifier.PROFILE_END:
                {
                    closeActiveProfile("normal");
                }
                break;
            // ==========================================
            // Cross-Section (I00..I99)
            // ==========================================
            case SIMAElementClassifier.CROSS_SECTION_BEGIN:
                {
                    closeAllOpenBlocks("crossSection", lineIndex + 1);
                    closeActiveCrossSection("new cross section (I00)", lineIndex + 1);
                    const alignment_number = parseIntSafe(tokens[1], 1);
                    const alignment_name = tokens[2] || "";
                    currentCrossSectionGroup = {
                        alignment_number,
                        alignment_name,
                        stations: [],
                    };
                }
                break;
            case SIMAElementClassifier.CROSS_SECTION_CENTER:
                {
                    if (currentCrossSectionGroup) {
                        if (currentCrossSectionStation) {
                            currentCrossSectionGroup.stations.push(currentCrossSectionStation);
                        }
                        const section_name = tokens[1] || "";
                        const chainage = parseFloatSafe(tokens[2], 0);
                        const ground_elevation = parseFloatSafe(tokens[3], 0);
                        const stake_elevation = parseFloatSafe(tokens[4], 0);
                        currentCrossSectionStation = {
                            center: {
                                classifier: SIMAElementClassifier.CROSS_SECTION_CENTER,
                                section_name,
                                chainage,
                                ground_elevation,
                                stake_elevation,
                            },
                            transversePoints: [],
                        };
                    }
                }
                break;
            case SIMAElementClassifier.CROSS_SECTION_POINT:
                {
                    if (currentCrossSectionStation) {
                        const point_name = tokens[1] || "";
                        const offset_distance = parseFloatSafe(tokens[2], 0);
                        const ground_elevation = parseFloatSafe(tokens[3], 0);
                        currentCrossSectionStation.transversePoints.push({
                            classifier: SIMAElementClassifier.CROSS_SECTION_POINT,
                            point_name,
                            offset_distance,
                            ground_elevation,
                        });
                    }
                }
                break;
            case SIMAElementClassifier.CROSS_SECTION_END:
                {
                    closeActiveCrossSection("normal");
                }
                break;
            // ==========================================
            // Parcels / Cadastral (D00..D99)
            // ==========================================
            case SIMAElementClassifier.PARCEL_BEGIN:
                {
                    closeAllOpenBlocks("parcel", lineIndex + 1);
                    closeActiveParcel("new parcel (D00)", lineIndex + 1);
                    const parcel_number = parseIntSafe(tokens[1], 1);
                    const lot_number = parseIntSafe(tokens[2], 1);
                    const parcel_type = parseIntSafe(tokens[3], 1);
                    const parcel_name = tokens[4] || undefined;
                    const begin = {
                        classifier: SIMAElementClassifier.PARCEL_BEGIN,
                        parcel_number,
                        lot_number,
                        parcel_type,
                        parcel_name,
                    };
                    currentParcel = {
                        begin,
                        points: [],
                    };
                }
                break;
            case SIMAElementClassifier.PARCEL_END:
                {
                    closeActiveParcel("normal");
                }
                break;
            // ==========================================
            // Triangulation / Sanshahō (E00..E99)
            // ==========================================
            case SIMAElementClassifier.TRIANGULATION_BEGIN:
                {
                    closeAllOpenBlocks("triangulation", lineIndex + 1);
                    closeActiveTriangulation("new triangulation (E00)", lineIndex + 1);
                    const block_number = parseIntSafe(tokens[1], 1);
                    const block_name = tokens[2] || "";
                    const lot_number = tokens[3] && tokens[3].trim() !== ""
                        ? parseIntSafe(tokens[3])
                        : undefined;
                    const begin = {
                        classifier: SIMAElementClassifier.TRIANGULATION_BEGIN,
                        block_number,
                        block_name,
                        lot_number,
                    };
                    currentTriangulation = {
                        begin,
                        triangles: [],
                    };
                }
                break;
            case SIMAElementClassifier.TRIANGULATION_VERTEX:
                {
                    if (currentTriangulation) {
                        if (currentTriangle?.vertex) {
                            currentTriangulation.triangles.push(currentTriangle);
                        }
                        // Handle both E01,triangle_no,ptA_no,ptA_name,ptB_no,ptB_name,ptC_no,ptC_name (8 tokens)
                        // and E01,ptA_no,ptA_name,ptB_no,ptB_name,ptC_no,ptC_name (7 tokens)
                        let triangle_number;
                        let ptA_number;
                        let ptA_name;
                        let ptB_number;
                        let ptB_name;
                        let ptC_number;
                        let ptC_name;
                        if (tokens.length >= 8) {
                            triangle_number = parseIntSafe(tokens[1]);
                            ptA_number = parseIntSafe(tokens[2]);
                            ptA_name = tokens[3] || "";
                            ptB_number = parseIntSafe(tokens[4]);
                            ptB_name = tokens[5] || "";
                            ptC_number = parseIntSafe(tokens[6]);
                            ptC_name = tokens[7] || "";
                        }
                        else {
                            ptA_number = parseIntSafe(tokens[1]);
                            ptA_name = tokens[2] || "";
                            ptB_number = parseIntSafe(tokens[3]);
                            ptB_name = tokens[4] || "";
                            ptC_number = parseIntSafe(tokens[5]);
                            ptC_name = tokens[6] || "";
                        }
                        const vertex = {
                            classifier: SIMAElementClassifier.TRIANGULATION_VERTEX,
                            triangle_number,
                            ptA_number,
                            ptA_name,
                            ptB_number,
                            ptB_name,
                            ptC_number,
                            ptC_name,
                        };
                        currentTriangle = { vertex };
                    }
                }
                break;
            case SIMAElementClassifier.TRIANGULATION_DIMENSION:
                {
                    if (currentTriangulation) {
                        // Handle E03,triangle_no,side_ab,side_bc,side_ca,base,height (7 tokens)
                        // or E03,side_ab,side_bc,side_ca,base,height (6 tokens)
                        let triangle_number;
                        let side_ab;
                        let side_bc;
                        let side_ca;
                        let base;
                        let height;
                        if (tokens.length >= 7) {
                            triangle_number = parseIntSafe(tokens[1]);
                            side_ab = parseFloatSafe(tokens[2]);
                            side_bc = parseFloatSafe(tokens[3]);
                            side_ca = parseFloatSafe(tokens[4]);
                            base = parseFloatSafe(tokens[5]);
                            height = parseFloatSafe(tokens[6]);
                        }
                        else {
                            side_ab = parseFloatSafe(tokens[1]);
                            side_bc = parseFloatSafe(tokens[2]);
                            side_ca = parseFloatSafe(tokens[3]);
                            base = parseFloatSafe(tokens[4]);
                            height = parseFloatSafe(tokens[5]);
                        }
                        const dimension = {
                            classifier: SIMAElementClassifier.TRIANGULATION_DIMENSION,
                            triangle_number,
                            side_ab,
                            side_bc,
                            side_ca,
                            base,
                            height,
                            area: (base * height) / 2.0,
                        };
                        if (currentTriangle) {
                            currentTriangle.dimension = dimension;
                        }
                        else {
                            currentTriangle = {
                                vertex: {
                                    classifier: SIMAElementClassifier.TRIANGULATION_VERTEX,
                                    triangle_number,
                                    ptA_number: 0,
                                    ptA_name: "",
                                    ptB_number: 0,
                                    ptB_name: "",
                                    ptC_number: 0,
                                    ptC_name: "",
                                },
                                dimension,
                            };
                        }
                    }
                }
                break;
            case SIMAElementClassifier.TRIANGULATION_END:
                {
                    closeActiveTriangulation("normal");
                }
                break;
            default:
                {
                    if (currentRoute) {
                        const unknownElem = {
                            classifier,
                            rawTokens: tokens,
                            rawLine: line,
                        };
                        currentRoute.routeElements.push(unknownElem);
                    }
                }
                break;
        }
    }
    // Close any unclosed blocks
    closeAllOpenBlocks("end of file", lines.length);
    return {
        comment,
        fileAttributeData,
        surveyPoints,
        simaRoutes,
        profiles: profiles.length > 0 ? profiles : undefined,
        crossSections: crossSections.length > 0 ? crossSections : undefined,
        parcels: parcels.length > 0 ? parcels : undefined,
        triangulations: triangulations.length > 0 ? triangulations : undefined,
        warnings: warnings.length > 0 ? warnings : undefined,
    };
}
//# sourceMappingURL=parser.js.map